"use client";

import { cn } from "@scott/ui/client";
import { Button } from "@ui-d2c/components/ui";
import Image from "next/image";

export type ErrorPageProps = {
  errorCode?: "404" | "500";
  title?: string;
  description?: string[];
  buttonText?: string;
  onButtonClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
  className?: string;
};

export function ErrorPage(props: ErrorPageProps) {
  const {
    errorCode,
    title,
    description,
    buttonText,
    onButtonClick,
    imageSrc = "/assets/404-500-background.jpg",
    imageAlt = "Error page background",
    className,
  } = props;

  // Substring length for unique key generation (prevent collision for similar lines)
  const KEY_SUBSTRING_LENGTH = 20;

  return (
    <div
      className={cn(
        "relative z-0 flex aspect-[16/9] h-screen w-full items-center justify-center overflow-hidden",
        className
      )}
    >
      {/* Background Image - 16:9 aspect ratio with cover */}
      <div className="absolute inset-0">
        <div className="relative h-full w-full">
          <Image
            alt={imageAlt}
            className="object-cover"
            fill
            priority
            sizes="100vw"
            src={imageSrc}
          />
        </div>
      </div>

      {/* Content Box - Dynamic height based on content */}
      <div className="relative z-10 mx-4 flex w-full max-w-[548px] items-center justify-center rounded-lg bg-white/80 p-8 text-center shadow-xl backdrop-blur-sm md:p-12">
        <div className="space-y-7">
          <div className="space-y-2">
            {/* Error Code */}
            <h1 className="font-bold text-4xl text-black">{errorCode}</h1>

            {/* Title */}
            <h2 className="font-bold text-black text-xl">{title}</h2>
          </div>
          {/* Description */}
          {description ? (
            <div className="space-y-2">
              {description.map((line, index) => (
                <p
                  className="text-black text-xl"
                  key={`line-${index}-${line.substring(0, KEY_SUBSTRING_LENGTH)}`}
                >
                  {line}
                </p>
              ))}
            </div>
          ) : null}

          {buttonText && onButtonClick ? (
            <Button
              className="rounded-lg px-6 py-3 font-medium"
              onClick={onButtonClick}
              size="lg"
            >
              {buttonText}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
